<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Restart Logları</span>
            <div>
              <v-btn color="primary" @click="refreshLogs" :loading="loading">
                <v-icon left>mdi-refresh</v-icon>
                Yenile
              </v-btn>
            </div>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card>
          <!-- İstatistikler -->
          <v-card-text v-if="stats">
            <v-row>
              <v-col cols="12" md="3">
                <v-card color="blue lighten-4" class="pa-3">
                  <div class="text-h6">{{ stats.total }}</div>
                  <div class="text-caption">Toplam Restart</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="green lighten-4" class="pa-3">
                  <div class="text-h6">{{ stats.successful }}</div>
                  <div class="text-caption">Başarılı</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="red lighten-4" class="pa-3">
                  <div class="text-h6">{{ stats.failed }}</div>
                  <div class="text-caption">Başarısız</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="orange lighten-4" class="pa-3">
                  <div class="text-h6">{{ stats.last24Hours }}</div>
                  <div class="text-caption">Son 24 Saat</div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Filtreler -->
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-select v-model="filters.restartType" :items="restartTypes" label="Restart Türü" clearable
                  @change="applyFilters"></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-select v-model="filters.success" :items="successOptions" label="Durum" clearable
                  @change="applyFilters"></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="filters.search" label="Ara (Client, Server, Sebep)" append-icon="mdi-magnify"
                  @input="debounceSearch" clearable></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Tablo -->
          <v-data-table :headers="headers" :items="logs" :loading="loading" :server-items-length="pagination.totalItems"
            :options.sync="tableOptions" @update:options="updateTable" class="elevation-1">
            <template v-slot:item.restart_type="{ item }">
              <v-chip :color="getRestartTypeColor(item.restart_type)" small text-color="white">
                {{ getRestartTypeText(item.restart_type) }}
              </v-chip>
            </template>

            <template v-slot:item.success="{ item }">
              <v-icon :color="item.success ? 'green' : 'red'" small>
                {{ item.success ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
            </template>

            <template v-slot:item.restarted_at="{ item }">
              {{ formatDate(item.restarted_at) }}
            </template>

            <template v-slot:item.client="{ item }">
              <div v-if="item.client">
                <div class="font-weight-medium">{{ item.client.uuid }}</div>
                <div class="text-caption">{{ item.client.manufacturer }} {{ item.client.model }}</div>
                <div class="text-caption text--secondary">{{ item.client.dataloggerName }}</div>
              </div>
              <span v-else class="text--disabled">Bilinmiyor</span>
            </template>

            <template v-slot:item.server="{ item }">
              <div v-if="item.server">
                <div class="font-weight-medium">{{ item.server.name }}</div>
                <div class="text-caption">{{ item.server.url }}</div>
              </div>
            </template>

            <template v-slot:item.triggered_by="{ item }">
              {{ item.triggered_by || 'Bilinmiyor' }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn icon small @click="showDetails(item)">
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Detay Dialog -->
    <v-dialog v-model="detailDialog" max-width="600px">
      <v-card v-if="selectedLog">
        <v-card-title>
          Restart Log Detayları
          <v-spacer></v-spacer>
          <v-btn icon @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <strong>ID:</strong> {{ selectedLog.id }}
            </v-col>
            <v-col cols="6">
              <strong>Tarih:</strong> {{ formatDate(selectedLog.restarted_at) }}
            </v-col>
            <v-col cols="6">
              <strong>Tür:</strong>
              <v-chip :color="getRestartTypeColor(selectedLog.restart_type)" small text-color="white">
                {{ getRestartTypeText(selectedLog.restart_type) }}
              </v-chip>
            </v-col>
            <v-col cols="6">
              <strong>Durum:</strong>
              <v-icon :color="selectedLog.success ? 'green' : 'red'" small>
                {{ selectedLog.success ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
              {{ selectedLog.success ? 'Başarılı' : 'Başarısız' }}
            </v-col>
            <v-col cols="12">
              <strong>Tetikleyen:</strong> {{ selectedLog.triggered_by || 'Bilinmiyor' }}
            </v-col>
            <v-col cols="12" v-if="selectedLog.reason">
              <strong>Sebep:</strong> {{ selectedLog.reason }}
            </v-col>
            <v-col cols="12" v-if="selectedLog.error_message">
              <strong>Hata Mesajı:</strong>
              <v-alert type="error" dense class="mt-2">
                {{ selectedLog.error_message }}
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// axios artık $api olarak global olarak mevcut

export default {
  name: 'RestartLogs',
  data() {
    return {
      logs: [],
      stats: null,
      loading: false,
      detailDialog: false,
      selectedLog: null,
      searchTimeout: null,
      pagination: {
        totalItems: 0,
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 25
      },
      tableOptions: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['restarted_at'],
        sortDesc: [true]
      },
      filters: {
        restartType: null,
        success: null,
        search: ''
      },
      headers: [
        { text: 'ID', value: 'id', width: '80px' },
        { text: 'Tarih', value: 'restarted_at', width: '180px' },
        { text: 'Tür', value: 'restart_type', width: '150px' },
        { text: 'Durum', value: 'success', width: '100px', align: 'center' },
        { text: 'Client', value: 'client', sortable: false },
        { text: 'Server', value: 'server', sortable: false },
        { text: 'Tetikleyen', value: 'triggered_by', width: '120px' },
        { text: 'Sebep', value: 'reason' },
        { text: 'İşlemler', value: 'actions', sortable: false, width: '100px' }
      ],
      restartTypes: [
        'Manuel',
        'Otomatik - Kayıt',
        'Otomatik - Gerçek Zamanlı',
        'Otomatik - Geçmiş',
        'Otomatik - Çoklu Geçmiş'
      ],
      restartTypeValues: {
        'Manuel': 'manual',
        'Otomatik - Kayıt': 'auto_save',
        'Otomatik - Gerçek Zamanlı': 'auto_realtime',
        'Otomatik - Geçmiş': 'auto_history',
        'Otomatik - Çoklu Geçmiş': 'auto_history_multi'
      },
      successOptions: [
        'Başarılı',
        'Başarısız'
      ],
      successValues: {
        'Başarılı': 'true',
        'Başarısız': 'false'
      }
    }
  },
  mounted() {
    this.loadLogs()
    this.loadStats()
  },
  watch: {
    'filters.restartType'() {
      this.applyFilters()
    },
    'filters.success'() {
      this.applyFilters()
    }
  },
  methods: {
    async loadLogs() {
      this.loading = true
      try {
        const params = {
          page: this.tableOptions.page,
          limit: this.tableOptions.itemsPerPage
        }

        // Filtreleri ekle
        if (this.filters.restartType) {
          params.restartType = this.restartTypeValues[this.filters.restartType]
        }
        if (this.filters.success !== null) {
          params.success = this.successValues[this.filters.success]
        }
        if (this.filters.search) {
          params.search = this.filters.search
        }

        const response = await this.$api.get('/api/restart-logs', { params })
        this.logs = response.data.logs
        this.pagination = response.data.pagination
      } catch (error) {
        console.error('Restart logları yüklenirken hata:', error)
        console.error('Restart logları yüklenemedi')
      } finally {
        this.loading = false
      }
    },
    async loadStats() {
      try {
        const response = await this.$api.get('/api/restart-logs/stats')
        this.stats = response.data
      } catch (error) {
        console.error('İstatistikler yüklenirken hata:', error)
      }
    },
    updateTable() {
      this.loadLogs()
    },
    applyFilters() {
      this.tableOptions.page = 1
      this.loadLogs()
      this.loadStats()
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.applyFilters()
      }, 500)
    },
    refreshLogs() {
      this.loadLogs()
      this.loadStats()
    },
    showDetails(log) {
      this.selectedLog = log
      this.detailDialog = true
    },
    getRestartTypeColor(type) {
      const colors = {
        'manual': 'blue',
        'auto_save': 'green',
        'auto_realtime': 'orange',
        'auto_history': 'purple',
        'auto_history_multi': 'indigo'
      }
      return colors[type] || 'grey'
    },
    getRestartTypeText(type) {
      const texts = {
        'manual': 'Manuel',
        'auto_save': 'Oto-Kayıt',
        'auto_realtime': 'Oto-Gerçek Zamanlı',
        'auto_history': 'Oto-Geçmiş',
        'auto_history_multi': 'Oto-Çoklu Geçmiş'
      }
      return texts[type] || type
    },
    formatDate(dateString) {
      if (!dateString) return 'Bilinmiyor'

      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return 'Geçersiz Tarih'

        return date.toLocaleString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return 'Geçersiz Tarih'
      }
    }
  }
}
</script>
